// Practice Settings → Bookings → Appointment Settings.
//
// A practice defines its own appointment statuses here: what each is called,
// what colour and icon it carries, whether it shows on the calendar by default,
// and — most importantly — its **type**.
//
// The type is what gives a status its behaviour. "Cancelled by Patient" typed
// as Cancelled frees the slot, suppresses reminders and greys the row out
// exactly the way the built-in Cancelled does. That is why the type dropdown
// carries a plain-English description rather than just a name.
//
// Structured on ViewIntakeSettings.jsx: practice selector, view/edit toggle,
// audit stamp, and a client-side merge before PUT (the server replaces
// sub-objects rather than merging them).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getPractice } from "../../api";
import { getAppointmentStatuses } from "../../api";
import "../Accounts/acc-list.css";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// Mirrors ALLOWED_COLORS in backend/src/services/appointmentStatusService.js.
// A fixed palette rather than a colour input: CLAUDE.md rules out inventing
// colours, and the server validates against the same list.
const PALETTE = [
  "#b5e2ff", "#fd7e14", "#198754", "#dc3545", "#21D1D4",
  "#0f3490", "#6c757d", "#38040E", "#D3D3D3",
  "#ef4444", "#f97316", "#f59e0b", "#16a34a", "#14b8a6",
  "#3b82f6", "#a855f7", "#ec4899", "#640314",
];

const ICONS = [
  "fa-circle-question", "fa-calendar-check", "fa-calendar-xmark",
  "fa-circle-check", "fa-circle-xmark", "fa-ban", "fa-triangle-exclamation",
  "fa-location-dot", "fa-spinner", "fa-clock", "fa-hourglass-half",
  "fa-user", "fa-user-check", "fa-user-xmark", "fa-user-clock",
  "fa-phone", "fa-video", "fa-stethoscope", "fa-flag", "fa-star",
  "fa-bell", "fa-arrow-rotate-left",
];

// What each type means, in the practice's language rather than the code's.
const TYPES = [
  { value: "Pending", label: "Pending", help: "A new booking that is not confirmed yet." },
  { value: "Confirmed", label: "Confirmed", help: "A booking the patient has confirmed." },
  { value: "NewPatient", label: "New Patient", help: "Awaiting patient details. Cannot be completed directly, and offers the intake form." },
  { value: "Attended", label: "Patient Arrived", help: "The patient is here or in the room. Counts as attended." },
  { value: "Completed", label: "Completed", help: "Finished and billable. Locks the invoice form and enables the receipt." },
  { value: "Cancelled", label: "Cancelled", help: "Frees the slot, sends no reminder, and greys the appointment out." },
  { value: "NoShow", label: "No Show", help: "Frees the slot and records a missed appointment." },
  { value: "Scheduling", label: "Unavailable", help: "Practitioner unavailability. Never offered as a choice and blocks online booking." },
  { value: "General", label: "Other", help: "Behaves like a normal booking. Use for a status nothing else fits." },
];

// Types the application writes to. Archiving the last live status of one is
// refused — this is what replaces freezing particular status names.
const REQUIRED_TYPES = [
  "Scheduling", "NewPatient", "Pending", "Confirmed",
  "Attended", "Completed", "Cancelled",
];

// Which type each part of the app writes, so the pane can show a status's role.
const WRITE_TARGET_NOTE = {
  Pending: "New appointments start here",
  Confirmed: "Set when a practice is chosen, and by online booking",
  NewPatient: "Set for a booking with no patient record yet",
  Attended: "Set when a patient checks in",
  Completed: "Set when notes are finished or an invoice is created",
  Scheduling: "Used for generated availability blocks",
};

// "fa-user-xmark" -> "user xmark", for the icon button's tooltip and label.
const iconName = (icon) => icon.replace("fa-", "").replace(/-/g, " ");

/**
 * Colour and icon pickers.
 *
 * A native `<select>` can't do this job — an option list renders text only, so
 * you would be choosing a colour by hex and an icon by name without seeing
 * either. A Bootstrap `Dropdown` gives the compactness of a select while
 * keeping the choices visual: the toggle shows the current selection and the
 * menu holds the grid these two used to occupy inline.
 *
 * The toggle borrows `.form-select` so it sits in the row as a peer of the Name
 * input and the Kind select — same border, height, radius and focus ring —
 * rather than reading as a button. `.form-select` draws its own caret on the
 * right, so `.acc-status-picker` suppresses Bootstrap's `.dropdown-toggle`
 * triangle in acc-list.css; otherwise there would be two.
 *
 * Icons are drawn in the status's own colour, so the toggle doubles as the
 * preview of how the status will appear on the calendar.
 */
function PickerToggle({ label, children }) {
  return (
    <Dropdown.Toggle
      as="button"
      type="button"
      className="form-select form-select-sm acc-status-picker d-flex align-items-center"
      aria-label={label}
      title={label}
    >
      {children}
    </Dropdown.Toggle>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <Dropdown>
      <PickerToggle label={`Colour: ${value}`}>
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: value,
            border: "1px solid var(--acc-border-color, rgba(15, 52, 144, 0.1))",
            flexShrink: 0,
          }}
        />
      </PickerToggle>
      <Dropdown.Menu className="shadow-sm p-2" style={{ minWidth: 0 }}>
        <div
          className="d-flex flex-wrap gap-1"
          role="radiogroup"
          aria-label="Status colour"
          style={{ width: 134 }}
        >
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={value === c}
              aria-label={`Colour ${c}`}
              title={c}
              onClick={() => onChange(c)}
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: c,
                border:
                  value === c
                    ? "3px solid var(--acc-brand-primary, #0f3490)"
                    : "1px solid var(--acc-border-color, rgba(15, 52, 144, 0.1))",
                padding: 0,
              }}
            />
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function IconPicker({ value, color, onChange }) {
  return (
    <Dropdown>
      <PickerToggle label={`Icon: ${iconName(value)}`}>
        <i className={`fa-solid ${value}`} style={{ color, width: 16 }} />
      </PickerToggle>
      <Dropdown.Menu className="shadow-sm p-2" style={{ minWidth: 0 }}>
        <div
          className="d-flex flex-wrap gap-1"
          role="radiogroup"
          aria-label="Status icon"
          style={{ width: 196 }}
        >
          {ICONS.map((i) => {
            const selected = value === i;
            return (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={iconName(i)}
                title={iconName(i)}
                onClick={() => onChange(i)}
                style={{
                  width: 28,
                  height: 28,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 6,
                  background: selected ? "var(--acc-brand-tint, #cfe2ff)" : "transparent",
                  border: selected
                    ? "2px solid var(--acc-brand-primary, #0f3490)"
                    : "1px solid var(--acc-border-color, rgba(15, 52, 144, 0.1))",
                  color,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                <i className={`fa-solid ${i}`} style={{ fontSize: "0.9rem" }} />
              </button>
            );
          })}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function ViewAppointmentSettings({ refresh }) {
  const [practices, setPractices] = useState([]);
  const [practice, setPractice] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [statuses, setStatuses] = useState([]);
  // Drag-to-reorder: the row being dragged, and the row it is currently over.
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ---------------- load ---------------- */

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem("user");
        const decoded = token ? jwtDecode(token) : {};
        const all = await getPractice();
        const mine = (all || []).filter((p) =>
          [].concat(decoded.practice || []).includes(p.practiceNumber)
        );
        const sorted = [...mine].sort((a, b) =>
          (a.practiceName || "").localeCompare(b.practiceName || "")
        );
        setPractices(sorted);
        if (sorted.length) {
          setPractice(sorted[0]);
          setSelectedId(sorted[0]._id);
        }
      } catch {
        setError("Could not load practices.");
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  // Read through the API rather than the practice document, so an unconfigured
  // practice comes back with the seeded defaults already filled in.
  useEffect(() => {
    if (!practice?.practiceNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAppointmentStatuses(practice.practiceNumber);
        if (!cancelled) setStatuses(data.statuses || []);
      } catch {
        if (!cancelled) setError("Could not load appointment statuses.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practice]);

  const handlePracticeSelect = (id) => {
    setSelectedId(id);
    setPractice(practices.find((p) => String(p._id) === String(id)) || null);
    setEditing(false);
    setError(null);
  };

  /* ---------------- derived ---------------- */

  const live = useMemo(() => statuses.filter((s) => !s.archived), [statuses]);

  // The status each type currently resolves to — the first live one of that
  // type. Reordering changes it, which is why the pane labels it inline.
  const writeTargets = useMemo(() => {
    const map = {};
    for (const s of live) if (!map[s.type]) map[s.type] = s.key;
    return map;
  }, [live]);

  const missingTypes = useMemo(
    () => REQUIRED_TYPES.filter((t) => !live.some((s) => s.type === t)),
    [live]
  );

  /* ---------------- mutations ---------------- */

  const update = (key, patch) =>
    setStatuses((prev) =>
      prev.map((s) => (s.key === key ? { ...s, ...patch } : s))
    );

  const move = (index, delta) =>
    setStatuses((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  // Drag-to-reorder lifts the row out and drops it at the target position,
  // rather than swapping the two — dragging past several rows should land where
  // you let go, not trade places with whatever happens to be there.
  const reorder = (from, to) =>
    setStatuses((prev) => {
      if (from == null || to == null || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });

  const addStatus = () =>
    setStatuses((prev) => [
      ...prev,
      {
        key: `custom-${Date.now()}`,
        label: "",
        type: "General",
        color: PALETTE[9],
        icon: ICONS[0],
        showByDefault: true,
        archived: false,
      },
    ]);

  const buildAuditMeta = () => {
    const token = sessionStorage.getItem("user");
    const decoded = token ? jwtDecode(token) : {};
    return {
      updatedAt: new Date().toISOString(),
      updatedBy:
        `${decoded.firstName || ""} ${decoded.lastName || ""}`.trim() ||
        decoded.email ||
        "",
    };
  };

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${URL}/practice/${practice._id}`, {
        appointmentStatuses: statuses,
        appointmentStatusesMeta: buildAuditMeta(),
      });
      setEditing(false);
    } catch (e) {
      // The server validates, so surface its message — it names the offending
      // status or the missing type.
      setError(
        e?.response?.data?.error ||
          "Could not save. Check that every status has a name, colour and icon."
      );
    } finally {
      setSaving(false);
    }
  }, [practice, statuses]);

  const cancel = async () => {
    setEditing(false);
    setError(null);
    const data = await getAppointmentStatuses(practice.practiceNumber);
    setStatuses(data.statuses || []);
  };

  /* ---------------- render ---------------- */

  if (loading) {
    return (
      <Container fluid className="pt-2 acc-list">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading appointment settings…</p>
        </div>
      </Container>
    );
  }

  if (!practice) {
    return (
      <Container fluid className="pt-2 acc-list">
        <p>No practices available.</p>
      </Container>
    );
  }

  const canSave = editing && missingTypes.length === 0 && live.every((s) => s.label.trim());

  return (
    <Container fluid className="pt-2 acc-list">
      <Row className="mb-3">
        <Col md={4}>
          <Form.Label>Practice</Form.Label>
          <Form.Select
            value={selectedId}
            onChange={(e) => handlePracticeSelect(e.target.value)}
            disabled={practices.length <= 1 || editing}
          >
            {practices.map((p) => (
              <option key={p._id} value={p._id}>
                {p.practiceName}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Appointment statuses are configured per practice.
          </Form.Text>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {editing && missingTypes.length > 0 && (
        <Alert variant="warning">
          Every practice needs at least one active status of each of these kinds:{" "}
          <strong>
            {missingTypes
              .map((t) => TYPES.find((x) => x.value === t)?.label || t)
              .join(", ")}
          </strong>
          . Add one, or un-archive an existing status, before saving.
        </Alert>
      )}

      <div className="d-flex align-items-center mb-2">
        <h5 className="text-primary mb-0">Appointment Statuses</h5>
        {!editing && (
          <Button
            variant="primary"
            className="ms-auto"
            onClick={() => setEditing(true)}
          >
            <i className="fa-solid fa-pen me-2" />
            Edit
          </Button>
        )}
      </div>

      <p className="text-muted small">
        A status&rsquo;s <strong>kind</strong> decides how it behaves — whether it
        frees the slot, sends reminders, or counts as attended. Rename anything
        you like; the kind is what the rest of the system reads.
      </p>

      {/* Desktop. Ordering is set by dragging a row's grip; the mobile cards
          below keep the arrow buttons, since HTML5 drag events don't fire for
          touch. */}
      <div className="d-none d-lg-block">
        <div className="border rounded">
          {statuses.map((s, index) => (
            <StatusRow
              key={s.key}
              status={s}
              index={index}
              total={statuses.length}
              editing={editing}
              isWriteTarget={writeTargets[s.type] === s.key}
              onChange={(patch) => update(s.key, patch)}
              onMove={(delta) => move(index, delta)}
              drag={
                editing
                  ? {
                      isDragging: dragIndex === index,
                      isOver:
                        overIndex === index &&
                        dragIndex !== null &&
                        dragIndex !== index,
                      onStart: () => setDragIndex(index),
                      onOver: () => setOverIndex(index),
                      onDrop: () => {
                        reorder(dragIndex, index);
                        setDragIndex(null);
                        setOverIndex(null);
                      },
                      onEnd: () => {
                        setDragIndex(null);
                        setOverIndex(null);
                      },
                    }
                  : null
              }
            />
          ))}
        </div>
      </div>

      {/* Mobile */}
      <div className="d-lg-none">
        {statuses.map((s, index) => (
          <div className="acc-card" key={s.key}>
            <div className="acc-card-top">
              <span className="acc-card-id">
                <i className={`fa-solid ${s.icon} me-2`} style={{ color: s.color }} />
                {s.label || <em className="text-muted">Unnamed</em>}
              </span>
              <Badge bg="secondary">
                {TYPES.find((t) => t.value === s.type)?.label || s.type}
              </Badge>
            </div>
            <div className="acc-card-body">
              <StatusRow
                status={s}
                index={index}
                total={statuses.length}
                editing={editing}
                isWriteTarget={writeTargets[s.type] === s.key}
                onChange={(patch) => update(s.key, patch)}
                onMove={(delta) => move(index, delta)}
                compact
              />
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="d-flex gap-2 mt-3">
          <Button variant="primary" onClick={save} disabled={!canSave || saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
          <Button variant="outline-secondary" onClick={cancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="outline-primary" className="ms-auto" onClick={addStatus}>
            <i className="fa-solid fa-plus me-2" />
            Add status
          </Button>
        </div>
      )}

      {practice.appointmentStatusesMeta?.updatedAt && (
        <p className="text-muted small mt-3 mb-0">
          Last updated {formatDate(practice.appointmentStatusesMeta.updatedAt)}
          {practice.appointmentStatusesMeta.updatedBy
            ? ` by ${practice.appointmentStatusesMeta.updatedBy}`
            : ""}
        </p>
      )}
    </Container>
  );
}

function StatusRow({
  status,
  index,
  total,
  editing,
  isWriteTarget,
  onChange,
  onMove,
  drag = null,
  compact = false,
}) {
  const typeMeta = TYPES.find((t) => t.value === status.type);
  const note = isWriteTarget ? WRITE_TARGET_NOTE[status.type] : null;
  // The drag image: the row as the user sees it, not the bare grip icon.
  const rowRef = useRef(null);

  // What the chosen kind does, and — when this status is the one the app writes
  // for its kind — what that means. Reached from the "i" beside the Kind label.
  const kindInfo = (
    <Popover>
      <Popover.Header as="h6" className="text-primary">
        {typeMeta?.label || status.type}
      </Popover.Header>
      <Popover.Body className="small">
        {typeMeta?.help}
        {note && (
          <div className="mt-2 text-primary">
            <i className="fa-solid fa-circle-info me-1" />
            {note} &mdash; move another status of this kind above it to change
            that.
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  if (!editing) {
    return (
      <div
        className={`d-flex align-items-center px-3 py-2 ${
          compact ? "" : "border-bottom"
        } ${status.archived ? "opacity-50" : ""}`}
      >
        <i
          className={`fa-solid ${status.icon} me-3`}
          style={{ color: status.color, width: 20 }}
        />
        <div className="flex-grow-1">
          <div className="fw-semibold">
            {status.label}
            {status.archived && (
              <Badge bg="secondary" className="ms-2">
                Archived
              </Badge>
            )}
          </div>
          <div className="small text-muted">
            {typeMeta?.label || status.type}
            {note && <span className="ms-2 text-primary">&middot; {note}</span>}
          </div>
        </div>
        {!status.showByDefault && !status.archived && (
          <Badge bg="light" text="dark">
            Hidden by default
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      className={`px-3 py-3 ${compact ? "" : "border-bottom"} ${
        drag?.isDragging ? "opacity-50" : ""
      }`}
      style={
        drag?.isOver ? { background: "var(--acc-brand-tint, #cfe2ff)" } : undefined
      }
      onDragOver={
        drag
          ? (e) => {
              // Without preventDefault the browser refuses the drop outright.
              e.preventDefault();
              drag.onOver();
            }
          : undefined
      }
      onDrop={
        drag
          ? (e) => {
              e.preventDefault();
              drag.onDrop();
            }
          : undefined
      }
    >
      {/* One row: the controls are narrow enough that both switches fit
          alongside them rather than on a second line. */}
      <Row className="g-2 align-items-end">
        {drag && (
          <Col xs="auto" className="d-flex align-items-center align-self-stretch">
            {/* The handle is the drag source rather than the whole row: a
                draggable container stops you selecting text inside its own
                inputs. Dragging it carries an image of the full row so what
                you see moving is what moves. */}
            <span
              className="acc-status-grip"
              draggable
              role="button"
              tabIndex={-1}
              aria-label={`Drag ${status.label || "status"} to reorder`}
              title="Drag to reorder"
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                // Firefox ignores a drag that sets no data.
                e.dataTransfer.setData("text/plain", String(index));
                if (rowRef.current)
                  e.dataTransfer.setDragImage(rowRef.current, 20, 20);
                // Deferred: the row fades while it is being dragged, and
                // changing the source element inside dragstart can cancel the
                // drag or bake the faded state into the drag image.
                setTimeout(drag.onStart, 0);
              }}
              onDragEnd={drag.onEnd}
            >
              <i className="fa-solid fa-grip-vertical" />
            </span>
          </Col>
        )}

        <Col md={3}>
          <Form.Label className="small mb-1">Name</Form.Label>
          <Form.Control
            size="sm"
            value={status.label}
            placeholder="e.g. Cancelled by Patient"
            onChange={(e) => onChange({ label: e.target.value })}
            isInvalid={!status.label.trim()}
          />
        </Col>

        <Col md={2}>
          <Form.Label className="small mb-1 d-flex align-items-center gap-1">
            Kind
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="right"
              overlay={kindInfo}
            >
              {/* The kind's description and the write-target note used to sit
                  below the row, which cost two lines on every status. The icon
                  goes primary rather than muted when there is a note, so the
                  row still signals that something applies to it. */}
              <Button
                variant="link"
                className={`p-0 border-0 lh-1 text-decoration-none ${
                  note ? "text-primary" : "text-muted"
                }`}
                aria-label={`What ${typeMeta?.label || status.type} does`}
                title={`What ${typeMeta?.label || status.type} does`}
              >
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </Form.Label>
          <Form.Select
            size="sm"
            value={status.type}
            onChange={(e) => onChange({ type: e.target.value })}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={6} md={1}>
          <Form.Label className="small mb-1">Colour</Form.Label>
          <ColorPicker
            value={status.color}
            onChange={(color) => onChange({ color })}
          />
        </Col>

        <Col xs={6} md={1}>
          <Form.Label className="small mb-1">Icon</Form.Label>
          <IconPicker
            value={status.icon}
            color={status.color}
            onChange={(icon) => onChange({ icon })}
          />
        </Col>

        <Col md={4} className="d-flex flex-wrap align-items-center gap-3">
          <Form.Check
            type="switch"
            className="acc-status-switch mb-0"
            id={`show-${status.key}`}
            label="Show on calendar"
            title="Ticked on when the calendar loads. Staff can still change it per session."
            checked={!!status.showByDefault}
            onChange={(e) => onChange({ showByDefault: e.target.checked })}
          />

          <Form.Check
            type="switch"
            className="acc-status-switch mb-0"
            id={`archived-${status.key}`}
            label="Archived"
            checked={!!status.archived}
            onChange={(e) => onChange({ archived: e.target.checked })}
          />

          {/* Touch devices get no HTML5 drag events, so the mobile cards keep
              buttons where the desktop rows have a grip. */}
          {!drag && (
            <div className="d-flex gap-2 ms-auto">
              <OverlayTrigger placement="top" overlay={<Tooltip>Move up</Tooltip>}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={index === 0}
                  onClick={() => onMove(-1)}
                  aria-label="Move up"
                >
                  <i className="fa-solid fa-chevron-up" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Move down</Tooltip>}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={index === total - 1}
                  onClick={() => onMove(1)}
                  aria-label="Move down"
                >
                  <i className="fa-solid fa-chevron-down" />
                </Button>
              </OverlayTrigger>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}
